import React from 'react';

import { schemas } from '@recurrency/core-api-schema';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { TransferTargetLineDTO } from '@recurrency/core-api-schema/dist/transferOrders/common';

import { ItemNotesPanelContent } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/ItemNotesPanelContent';
import { LocationStockPanelContent } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/LocationStockPanelContent';
import { ReplenishmentPathsPanelContent } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/ReplenishmentPathsPanelContent';
import { GenericTargetLineDTO } from 'pages/purchasing/PurchaseTargetsPage/types';

import { MiniTableSkeleton } from 'components/MiniTable';
import { InventoryUsagePanelContent } from 'components/recipes/sidePane/InventoryUsagePanelContent';
import { ItemLocationNotesPanelContent } from 'components/recipes/sidePane/ItemLocationNotesPanelContent';
import { LabelValueProperties, LabelValueProperty } from 'components/recipes/sidePane/LabelValueProperties';
import { SidePane } from 'components/recipes/sidePane/SidePane';
import { StockInheritancePanelContent } from 'components/recipes/sidePane/StockInheritancePanelContent';
import { UnitOfMeasuresPanelContent } from 'components/recipes/sidePane/UnitOfMeasuresPanelContent';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { getErpName } from 'utils/formatting';
import { ViewSettingKey } from 'utils/tableAndSidePaneSettings/types';
import { isTenantErpTypeNetSuite } from 'utils/tenants';
import { getTenantSetting, isHubAndSpokeEnabled } from 'utils/tenantSettings';

export function ItemSummarySidePane({ itemInfo }: { itemInfo: TransferTargetLineDTO }) {
  const isPlanningHubAndSpoke = isHubAndSpokeEnabled();
  const { activeTenant } = useGlobalApp();
  // hide item notes panel for netsuite since we don't sync item notes for netsuite tenants);
  const showItemNotesPanel = !isTenantErpTypeNetSuite(activeTenant.erpType);
  const showStockInheritance = !!getTenantSetting(TenantSettingKey.FeatureStockInheritance);
  const { data: itemDetailsData, isLoading: isItemDetailsLoading } = useCoreApi(schemas.items.getItemDetailsV2, {
    pathParams: { itemId: itemInfo.itemCode },
  });

  return (
    <SidePane
      title={`Item #${itemInfo.itemCode} From Location #${itemInfo.sourceLocationId} To Location #${itemInfo.destinationLocationId}`}
      settingKey={ViewSettingKey.TransferTargetLinesSidePane}
      description={itemInfo.itemName}
      extendedDescription={itemDetailsData?.itemDescription}
      content={
        isItemDetailsLoading ? (
          <MiniTableSkeleton />
        ) : (
          <LabelValueProperties
            properties={
              [
                itemInfo.itemGroupId && itemInfo.itemGroupName
                  ? {
                      label: 'Item Group',
                      value: `${itemInfo.itemGroupId}: ${itemInfo.itemGroupName}`,
                    }
                  : null,
                itemInfo.supplierId && itemInfo.supplierName
                  ? {
                      label: 'Primary Supplier',
                      value: `${itemInfo.supplierId}: ${itemInfo.supplierName}`,
                    }
                  : null,
                { label: 'Replenishment Method', value: itemInfo.destinationReplenishmentMethod },
              ] as Array<LabelValueProperty | null>
            }
          />
        )
      }
      panels={[
        isPlanningHubAndSpoke
          ? {
              settingKey: 'replenishmentPath',
              title: 'Replenishment Path',
              content: (
                <ReplenishmentPathsPanelContent
                  itemUid={itemInfo.itemId}
                  itemId={itemInfo.itemCode}
                  locationId={itemInfo.sourceLocationId}
                  isPurchasing
                />
              ),
            }
          : null,
        {
          settingKey: 'sourceUsage',
          title: 'Destination Location Usage',
          content: (
            <InventoryUsagePanelContent
              itemId={itemInfo.itemCode}
              itemUid={itemInfo.itemId}
              locations={[{ foreignId: itemInfo.destinationLocationId, name: itemInfo.destinationLocationName }]}
            />
          ),
        },
        {
          settingKey: 'destinationUsage',
          title: 'Source Location Usage',
          content: (
            <InventoryUsagePanelContent
              itemId={itemInfo.itemCode}
              itemUid={itemInfo.itemId}
              locations={[{ foreignId: itemInfo.sourceLocationId, name: itemInfo.sourceLocationName }]}
            />
          ),
        },
        showStockInheritance
          ? {
              settingKey: 'stockInheritance',
              title: 'Stock Inheritance',
              content: <StockInheritancePanelContent itemId={itemInfo.itemCode} itemUid={itemInfo.itemId} />,
            }
          : null,
        showItemNotesPanel
          ? {
              settingKey: 'itemNotes',
              title: `${getErpName(activeTenant.erpType)} Item Notes`,
              content: (
                <ItemNotesPanelContent
                  uniqueItems={[{ itemId: itemInfo.itemId, foreignId: itemInfo.itemCode, name: itemInfo.itemName }]}
                />
              ),
            }
          : null,
        {
          settingKey: 'itemLocationNotesSource',
          title: 'Recurrency Notes (Source Location)',
          content: (
            <ItemLocationNotesPanelContent
              itemLocationTitle={`${itemInfo.itemName} at ${itemInfo.sourceLocationName} and ${itemInfo.destinationLocationName}`}
              itemUid={itemInfo.itemId}
              locationId={itemInfo.sourceLocationId}
            />
          ),
          optional: true,
        },
        {
          settingKey: 'itemLocationNotesDestination',
          title: 'Recurrency Notes (Destination Location)',
          content: (
            <ItemLocationNotesPanelContent
              itemLocationTitle={`${itemInfo.itemName} at ${itemInfo.destinationLocationName} and ${itemInfo.destinationLocationName}`}
              itemUid={itemInfo.itemId}
              locationId={itemInfo.destinationLocationId}
            />
          ),
          optional: true,
        },
        {
          settingKey: 'locationStock',
          title: 'Location Stock',
          content: (
            <LocationStockPanelContent
              targetLine={
                {
                  itemId: itemInfo.itemCode,
                  status: itemInfo.status,
                } as GenericTargetLineDTO
              }
            />
          ),
        },
        {
          settingKey: 'unitOfMeasures',
          title: 'Unit of Measures',
          content: <UnitOfMeasuresPanelContent itemId={itemInfo.itemCode} />,
        },
      ].filter(Boolean)}
    />
  );
}
